import React, { useCallback, useRef, useState } from "react";
import Header from "./Header";
import selfieInstructionImg from "../assets/images/selfie-instruction-img.png";
import { Camera } from "react-camera-pro";
import "../assets/styles/SelfieScreenOne.css";
import errorExclamation from "../assets/images/triangle-exclamation.svg";
import { TOEFL_PROGRAM_ID } from "../constants/Constants";

const SelfieScreenTwo = ({
  onCapture,
  onError,
  showSelfieSuccess,
  selfieError,
  selfieStatus,
  isNextStepsEnabled,
  setSelfieError,
  setIsLoading,
  setLoadingMessage,
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const cameraRef = useRef(null);
  const programId = sessionStorage.getItem("program_id");

  // Detect Chinese browsers for specific optimizations
  const detectChineseBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isWeChat = userAgent.includes("micromessenger");
    const isUC =
      userAgent.includes("ucbrowser") || userAgent.includes("ubrowser");
    const isQQ =
      userAgent.includes("qqbrowser") || userAgent.includes("mqqbrowser");
    const isBaidu =
      userAgent.includes("baidubrowser") || userAgent.includes("bidubrowser");
    const isHuawei = userAgent.includes("huawei");
    const isXiaomi =
      userAgent.includes("xiaomi") || userAgent.includes("miuibrowser");
    const isSogou = userAgent.includes("sogou");

    return {
      isWeChat,
      isUC,
      isQQ,
      isBaidu,
      isHuawei,
      isXiaomi,
      isSogou,
      isChineseBrowser:
        isWeChat || isUC || isQQ || isBaidu || isHuawei || isXiaomi || isSogou,
    };
  };

  const browserInfo = detectChineseBrowser();

  // Get appropriate error message for Chinese browsers
  const getErrorMessage = useCallback(
    (error) => {
      const message = error.message ? error.message.toLowerCase() : "";

      if (
        browserInfo.isWeChat &&
        (message.includes("permission") || message.includes("notallowed"))
      ) {
        return "Camera permission denied in WeChat. Please open this page in your default browser (Safari/Chrome) for camera access.";
      }

      if (browserInfo.isUC && message.includes("permission")) {
        return "Camera permission denied. Please allow camera access in UC Browser settings.";
      }

      if (browserInfo.isQQ && message.includes("permission")) {
        return "Camera permission denied. Please allow camera access in QQ Browser settings.";
      }

      if (message.includes("notfound") || message.includes("devicenotfound")) {
        return "No camera found on this device. Please use a device with a camera.";
      }

      if (message.includes("permission") || message.includes("notallowed")) {
        return "Camera permission denied. Please allow camera access and refresh the page.";
      }

      if (
        message.includes("notsupported") ||
        message.includes("not supported")
      ) {
        return "Camera access not supported in this browser. Please try a different browser.";
      }

      if (message.includes("https") || message.includes("secure")) {
        return "Camera access requires HTTPS. Please use a secure connection.";
      }

      return "Camera access failed. Please check permissions and try again.";
    },
    [browserInfo]
  );

  const handleUseCamera = useCallback(async () => {
    setIsLoading(true);
    setError("");
    setSelfieError("");
    // Check HTTPS requirement
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      setError(
        "Camera access requires HTTPS connection. Please use a secure connection."
      );
      setIsLoading(false);
      return;
    }

    // WeChat browser specific handling
    if (browserInfo.isWeChat) {
      // For WeChat, we need to handle permission differently
      if (window.WeixinJSBridge) {
        // If in WeChat environment, provide fallback option
        setError(
          "Camera access may be limited in WeChat browser. Consider opening in default browser for better experience."
        );
      }
    }

    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera access not supported in this browser");
      }

      setShowCamera(true);
      setShowInstructions(false);
    } catch (err) {
      console.error("Camera initialization error:", err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [browserInfo.isWeChat, getErrorMessage]);

  const handleCapture = useCallback(() => {
    try {
      if (cameraRef.current) {
        const imageSrc = cameraRef.current.takePhoto();
        setCapturedImage(imageSrc);
      }
    } catch (err) {
      console.error("Capture error:", err);
      setError("Failed to capture photo. Please try again.");
    }
  }, []);

  const timer = (millis) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        setIsLoading(false);
        resolve();
      }, millis);
    });
  };

  const handleUsePhoto = useCallback(async () => {
    if (!capturedImage) return;
    if (count >= 6) {
      setCount(count + 1);
      handleClose();
      setSelfieError(
        "Please refresh the page and try again. If it keeps happening, try again later or contact support."
      );
      setIsLoading(true);
      await timer(2000);
      return;
    }

    if (!capturedImage) return;

    setLoadingMessage(["Selfie Captured Successfully", "Processing ...."]);
    setIsLoading(true);

    try {
      // Convert base64 to JPEG format for IDVaaS
      setCount(count + 1);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Convert to JPEG with high quality
        const jpegData = canvas.toDataURL("image/jpeg", 0.9);

        // Create file-like object for existing upload workflow
        fetch(jpegData)
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File([blob], "selfie.jpeg", {
              type: "image/jpeg",
            });
            onCapture(file, jpegData);
            handleClose();
          })
          .catch((err) => {
            console.error("Image conversion error:", err);
            setError("Failed to process image. Please try again.");
          });
      };

      img.onerror = () => {
        setError("Failed to process captured image. Please try again.");
      };

      img.src = capturedImage;
    } catch (err) {
      console.error("Photo processing error:", err);
      setError("Failed to process photo. Please try again.");
    }
  }, [capturedImage, onCapture]);

  // Handle retaking photo
  const handleRetake = () => {
    setCapturedImage(null);
    setError("");
  };

  // Close camera and reset state
  const handleClose = () => {
    setShowCamera(false);
    setShowInstructions(false);
    setCapturedImage(null);
    setError("");
  };

  // Camera error handler
  const handleCameraError = (error) => {
    console.error("Camera error:", error);
    setError(getErrorMessage(error));
    onError && onError(error);
  };

  return (
    <div className="selfie-screen-one">
      <Header />
      <div className="selfie-screen-container">
        {showCamera && capturedImage ? (
          <h2>Check your photo</h2>
        ) : (
          <h2>Take your photo</h2>
        )}
        {!showCamera && (
          <div>
            <div className="img-container">
              <img src={selfieInstructionImg} />
            </div>
            <div className="selfie-instructions">
              <h4>Make Sure you:</h4>
              <ul>
                <li>are in a well lit room</li>
                <li>look straight at the camera</li>
                <li>keep a plain expression (do not smile or frown)</li>
              </ul>
            </div>
            <p style={{ fontSize: "1rem" }}>
              You might need to try a few times to take a photo that meets the
              requirements
            </p>
          </div>
        )}

        {showCamera && (
          // <div className="camera-container">
          <div className="camera-viewport">
            {!capturedImage && (
              <>
                <Camera
                  ref={cameraRef}
                  aspectRatio={3 / 4}
                  facingMode="user"
                  errorMessages={{
                    noCameraAccessible:
                      "No camera found. Please connect a camera and refresh.",
                    permissionDenied:
                      "Camera permission denied. Please allow access and refresh.",
                    switchCamera: "Switch camera not supported on this device.",
                    canvas: "Canvas not supported in this browser.",
                  }}
                  onError={handleCameraError}
                />
                {/* Face Guide Oval Overlay */}
                <div
                  className="face-guide-overlay"
                  role="img"
                  aria-label="Face alignment guide"
                >
                  <div className="face-guide-oval" aria-hidden="true"></div>
                </div>
                <div className="camera-controls">
                  <button
                    className="capture-btn"
                    onClick={handleCapture}
                    aria-label="Capture your selfie photo"
                  >
                    Capture Photo
                  </button>
                  <button
                    className="close-camera-btn"
                    onClick={handleClose}
                    aria-label="Close camera and return to previous step"
                  >
                    Close Camera
                  </button>
                </div>
              </>
            )}
          </div>
          // </div>
        )}
      </div>
      {showCamera && capturedImage && (
        <div className="photo-preview">
          <img
            src={capturedImage}
            alt="Captured selfie"
            className="preview-image"
          />
          <p>Decide if this is the photo you want to use.</p>
          <div className="preview-controls">
            <button
              className={`retake-btn ${
                programId == TOEFL_PROGRAM_ID
                  ? "toefl-font-color"
                  : "gre-font-color"
              }`}
              onClick={handleRetake}
            >
              Retake
            </button>
            <button
              className={`use-photo-btn ${
                programId == TOEFL_PROGRAM_ID
                  ? "toefl-button-bg"
                  : "gre-button-bg"
              }`}
              onClick={handleUsePhoto}
            >
              Use This Photo
            </button>
          </div>
        </div>
      )}
      <div className="instructions-actions">
        <div>
          <p
            id="selfieStatus"
            style={{
              display: "none",
            }}
          ></p>
          {showSelfieSuccess && (
            <div className="success-message" role="status" aria-live="polite">
              <p className="success-text">
                ✓ Selfie captured successfully. You may now proceed with
                Complete Journey
              </p>
            </div>
          )}
          {selfieError && (
            <div className="error-message" role="alert" aria-live="polite">
              <p className="error-text">
                <img src={errorExclamation} style={{ alignSelf: "baseline" }} />
                {selfieError}
              </p>
              <p className="error-instruction">
                Please retake your selfie to continue.
              </p>
            </div>
          )}
          {selfieStatus && !showSelfieSuccess && !selfieError && (
            <div
              className="selfie-custom-status"
              role="status"
              aria-live="polite"
            >
              <p className="status-text">{selfieStatus}</p>
            </div>
          )}
        </div>
      </div>
      <div
        className="selfie-button-container"
        style={{ display: !showCamera ? "flex" : "none" }}
      >
        <button
          className={`continue-button ${
            programId == TOEFL_PROGRAM_ID ? "toefl-button-bg" : "gre-button-bg"
          }`}
          onClick={handleUseCamera}
          disabled={
            !isNextStepsEnabled ||
            (selfieStatus && !showSelfieSuccess && !selfieError) ||
            count >= 7
          }
        >
          Use Camera
        </button>
      </div>
    </div>
  );
};

export default SelfieScreenTwo;
